import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, Eye, MousePointer, Send, Calendar, CheckCircle,
  RotateCcw, Loader, RefreshCw, AlertCircle,
} from 'lucide-react';
import * as emailApi from '../../services/emailMarketingApi';

const CAMPAIGN_IMAGE_URL = "https://api.miplapp.in/storage/app/public/campaign-images/";
const POLL_INTERVAL      = 10000; // poll every 10 seconds to respect rate limits

const CampaignModal = ({ campaign: initialCampaign, onClose, onSendComplete }) => {
  const [campaign,     setCampaign]     = useState(initialCampaign);
  const [stats,        setStats]        = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [sendState,    setSendState]    = useState('idle'); // idle | starting | polling | complete | error
  const [errorMsg,     setErrorMsg]     = useState('');
  
  const pollRef       = useRef(null);
  // 🔥 FIX: Track the previous status so we don't cause an infinite refresh loop
  const prevStatusRef = useRef(initialCampaign.status);

  // ── Polling ────────────────────────────────────────────────────────────────
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const loadStats = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const response = await emailApi.getCampaignStats(campaign.id);
      const s = response.data;
      setStats(s);

      // Sync campaign state from the freshest data
      setCampaign(prev => ({
        ...prev,
        status          : s.campaign.status,
        total_processed : s.totals.total_sent,
        total_success   : s.totals.total_success,
        total_failure   : s.totals.total_failed,
        total_recipients: s.totals.total_recipients,
      }));

      // Check if done
      if (['completed', 'failed'].includes(s.campaign.status)) {
        stopPolling();
        setSendState(s.campaign.status === 'completed' ? 'complete' : 'error');
        
        // 🔥 FIX: Only tell the dashboard to refresh if the campaign JUST finished.
        // This prevents the infinite looping/flickering bug when viewing history.
        if (prevStatusRef.current === 'processing' || prevStatusRef.current === 'draft') {
          if (typeof onSendComplete === 'function') onSendComplete(campaign.id);
        }
      }
      
      // Update ref for the next cycle
      prevStatusRef.current = s.campaign.status;

    } catch (err) {
      if (err?.response?.status === 429) {
        console.warn('Rate limit reached (429). Skipping this poll cycle.');
      } else {
        console.error('Stats poll error:', err);
      }
    } finally {
      setStatsLoading(false);
      setRefreshing(false);
    }
  }, [campaign.id, stopPolling, onSendComplete]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(() => loadStats(true), POLL_INTERVAL);
  }, [loadStats, stopPolling]);

  useEffect(() => {
    loadStats();

    // If campaign is already processing (e.g. modal reopened mid-run), start polling
    if (initialCampaign.status === 'processing') {
      setSendState('polling');
      startPolling();
    }

    return () => stopPolling();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleStartSend = async () => {
    if (sendState === 'starting' || sendState === 'polling') return;

    setSendState('starting');
    setErrorMsg('');

    try {
      // ONE call — backend handles the rest
      await emailApi.sendCampaign(campaign.id);

      setSendState('polling');
      prevStatusRef.current = 'processing'; // update our ref
      await loadStats(true);
      startPolling();
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Failed to start campaign.';
      setErrorMsg(msg);
      setSendState('error');
    }
  };

  const handleRefresh = () => loadStats();

  const formatDate = (date) => new Date(date).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  // ── Derived state ──────────────────────────────────────────────────────────
  const isSendable  = ['draft', 'processing', 'failed'].includes(campaign.status);
  const isPolling   = sendState === 'polling';
  const isStarting  = sendState === 'starting';

  const totals      = stats?.totals ?? {};
  const processed   = totals.total_sent        ?? campaign.total_processed ?? 0;
  const success     = totals.total_success     ?? campaign.total_success   ?? 0;
  const failure     = totals.total_failed      ?? campaign.total_failure   ?? 0;
  const total       = totals.total_recipients  ?? campaign.total_recipients ?? 0;
  const pct         = totals.progress_percentage ?? (total > 0 ? (processed / total) * 100 : 0);
  const remaining   = Math.max(0, total - processed);

  const progressLabel =
    sendState === 'complete' ? '✅ Campaign Complete!'   :
    sendState === 'error'    ? '❌ Campaign Failed'      :
    isPolling                ? '📤 Sending in background…' :
    isStarting               ? '⏳ Starting…'            : '';

  const progressBarColor =
    sendState === 'complete' ? 'bg-green-500' :
    sendState === 'error'    ? 'bg-red-500'   :
    'bg-blue-500';

  const StatCard = ({ label, value, icon: Icon, color, suffix = '' }) => (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>
          <Icon size={18} color={color} />
        </div>
        <p className="text-sm text-gray-500">{label}</p>
      </div>
      <p className="text-2xl font-bold text-gray-900">
        {typeof value === 'number' ? value.toLocaleString() : value}{suffix}
      </p>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50 flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{campaign.subject}</h2>
            <div className="flex items-center gap-3 mt-1">
              <code className="text-xs text-gray-500">{campaign.code}</code>
              <span className="text-xs text-gray-400">
                <Calendar size={14} className="inline mr-1" />
                {formatDate(campaign.created_at)}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                campaign.status === 'completed'  ? 'bg-green-100 text-green-700'  :
                campaign.status === 'processing' ? 'bg-yellow-100 text-yellow-700':
                campaign.status === 'failed'     ? 'bg-red-100 text-red-700'     :
                'bg-gray-100 text-gray-600'
              }`}>
                {campaign.status}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {statsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500" />
            </div>
          ) : (
            <div className="space-y-6">

              {/* Refresh */}
              <div className="flex justify-end">
                <button onClick={handleRefresh} disabled={refreshing}
                  className="flex items-center gap-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 border rounded-lg hover:bg-gray-50 disabled:opacity-50">
                  <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                  Refresh Stats
                </button>
              </div>

              {/* Background-send notice */}
              {isPolling && (
                <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <Loader size={18} className="text-blue-600 animate-spin flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-blue-800">
                      Sending in the background
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      You can safely close this window. Stats refresh automatically every 10 seconds.
                    </p>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Total Sent"    value={processed}                                               icon={Send}         color="#2563eb" />
                <StatCard label="Success Rate"  value={processed > 0 ? ((success / processed) * 100).toFixed(1) : 0} icon={CheckCircle}  color="#059669" suffix="%" />
                <StatCard label="Open Rate"     value={totals.open_rate  ?? 0}                                  icon={Eye}          color="#7c3aed" suffix="%" />
                <StatCard label="Click Rate"    value={totals.click_rate ?? 0}                                  icon={MousePointer} color="#ea580c" suffix="%" />
              </div>

              {/* Progress bar */}
              {(isPolling || isStarting || ['complete', 'error'].includes(sendState)) && (
                <div className={`rounded-lg p-4 border ${
                  sendState === 'complete' ? 'bg-green-50 border-green-200' :
                  sendState === 'error'    ? 'bg-red-50 border-red-200'     :
                  'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-800">{progressLabel}</span>
                    <span className="text-sm font-medium text-gray-700">
                      {processed.toLocaleString()} / {total.toLocaleString()} &nbsp;({pct.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                    <div className={`h-3 rounded-full transition-all duration-700 ${progressBarColor}`}
                      style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>✔ {success.toLocaleString()} delivered</span>
                    {failure > 0 && <span className="text-red-500">✗ {failure.toLocaleString()} failed</span>}
                    {isPolling && <span>{remaining.toLocaleString()} remaining</span>}
                  </div>
                </div>
              )}

              {/* Resume bar (processing but not yet polling) */}
              {sendState === 'idle' && campaign.status === 'processing' && processed > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-yellow-800 mb-2">
                    ⚠ Campaign is processing in background — {processed}/{total} sent
                  </p>
                  <p className="text-xs text-yellow-700">
                    Click "Resume" to re-attach the live progress view, or just leave it — it will finish on its own.
                  </p>
                </div>
              )}

              {/* Error message */}
              {sendState === 'error' && errorMsg && (
                <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
                  <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{errorMsg}</p>
                </div>
              )}

              {/* Campaign details */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaign Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500 mb-1">Message Preview</p>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{campaign.message}</p>
                    </div>
                  </div>
                  {campaign.image_url && (
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500 mb-1">Campaign Image</p>
                      <div className="bg-white p-2 rounded-lg border border-gray-200 inline-block">
                        <img
                          src={`${CAMPAIGN_IMAGE_URL}${campaign.image_url.split('/').pop()}`}
                          alt="Campaign"
                          className="max-h-64 rounded-lg object-contain"
                          onError={e => { e.target.src = 'https://placehold.co/400x200?text=Image+Not+Found'; }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Batch stats */}
              {stats?.by_batch?.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Batch Statistics</h3>
                  <div className="space-y-2">
                    {stats.by_batch.map((batch, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Batch {batch.batch_count}</span>
                        <span className="text-gray-900 font-medium">
                          {batch.opens} opens, {batch.clicks} clicks
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-2 flex-shrink-0">
          {isSendable && !isPolling && campaign.status !== 'completed' && (
            <button onClick={handleStartSend} disabled={isStarting}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-colors ${
                campaign.status === 'processing'
                  ? 'bg-orange-600 hover:bg-orange-700'
                  : 'bg-green-600 hover:bg-green-700'
              } disabled:opacity-60`}>
              {isStarting
                ? <><Loader size={16} className="animate-spin" /> Starting…</>
                : campaign.status === 'processing'
                  ? <><RotateCcw size={16} /> Resume Watching</>
                  : <><Send size={16} /> Send Campaign</>}
            </button>
          )}

          {isPolling && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm">
              <Loader size={16} className="animate-spin" />
              Running in background — safe to close
            </div>
          )}

          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-900 border rounded-lg hover:bg-gray-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignModal;